import { useCallback, useState } from 'react';

export type ScanFeedback = 'success' | 'duplicate' | 'error' | null;

const FEEDBACK_CLASSES: Record<Exclude<ScanFeedback, null> | 'idle', string> = {
  success: 'border-green-500 bg-green-50',
  duplicate: 'border-amber-500 bg-amber-50',
  error: 'border-red-500 bg-red-50',
  idle: 'border-gray-200 bg-white',
};

/**
 * Audible + visual scanner feedback shared by every batch screen.
 * Success = high beep, duplicate/warning = mid beep, error = low buzz.
 */
export function useScanFeedback(initialSoundEnabled = true) {
  const [soundEnabled, setSoundEnabled] = useState(initialSoundEnabled);
  const [feedback, setFeedback] = useState<ScanFeedback>(null);

  const playFeedback = useCallback((kind: Exclude<ScanFeedback, null>) => {
    setFeedback(kind);
    window.setTimeout(() => setFeedback(null), 900);
    if (!soundEnabled) return;

    try {
      const context = new AudioContext();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.frequency.value = kind === 'success' ? 880 : kind === 'duplicate' ? 440 : 180;
      gain.gain.setValueAtTime(0.08, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.12);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.12);
      oscillator.addEventListener('ended', () => void context.close());
    } catch {
      // Visual feedback remains available when the browser blocks audio.
    }
  }, [soundEnabled]);

  const toggleSound = useCallback(() => setSoundEnabled((enabled) => !enabled), []);

  return {
    feedback,
    playFeedback,
    soundEnabled,
    setSoundEnabled,
    toggleSound,
    feedbackClasses: FEEDBACK_CLASSES[feedback ?? 'idle'],
  };
}
